import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { domainToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

const REGIONS = {
  us: { iroh: "visibility.amp.cisco.com", orbital: "orbital.amp.cisco.com" },
  eu: {
    iroh: "visibility.eu.amp.cisco.com",
    orbital: "orbital.eu.amp.cisco.com",
  },
  apjc: {
    iroh: "visibility.apjc.amp.cisco.com",
    orbital: "orbital.apjc.amp.cisco.com",
  },
} as const;

const region = (cfg: Record<string, string>) =>
  REGIONS[cfg.region as keyof typeof REGIONS] ?? REGIONS.us;

export const ciscoOrbital = defineConnector({
  identifier: "ciscoOrbital",
  name: "Cisco Orbital",
  tagLine: "Endpoint querying with osquery",
  logo,
  protocol: "http",
  tags: ["security"],
  defaultUrlPattern: (cfg) => domainToRegex(region(cfg).orbital),
  auth: oauthAuth,
  steps: [
    {
      title: "Create an API client for Orbital",
      items: [
        {
          kind: "markdown",
          content:
            "In the Cisco Secure Client Cloud Management console, go to **Administration > API Clients** and click **Generate API Client**. Assign both the **Admin** and **Orbital** scopes, then copy the **Client ID** and **Client Password** — the password is shown only once.",
        },
      ],
    },
    {
      title: "Enter your API credentials",
      items: [
        {
          kind: "select",
          label: "Region",
          required: true,
          defaultValue: "us",
          helperText:
            "The region your Cisco organization is hosted in. Credentials are valid in one region only.",
          target: { kind: "configuration", field: "region" },
          options: [
            { value: "us", label: "North America" },
            { value: "eu", label: "Europe" },
            { value: "apjc", label: "Asia Pacific (APJC)" },
          ],
        },
        {
          kind: "input",
          label: "Client ID",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client password",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "client_credentials",
    clientCredentialsFrom: "db",
    tokenEndpoint: (cfg) => `https://${region(cfg).iroh}/iroh/oauth2/token`,
    tokenEndpointAuthMethod: () => "client_secret_basic",
    scopes: () => [],
  },
  connectionTest: {
    protocol: "http",
    request: (cfg) => `https://${region(cfg).orbital}/v0/ok`,
  },
});
